import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2, Circle, CircleDot, Star, ChevronRight, Sparkles, MessageCircle,
  ClipboardList, Megaphone, CheckCircle, Rocket, FileText, MapPin, MessageSquare,
  LayoutGrid, Lightbulb, Phone, Mail, Clock, TrendingUp, Send, LayoutTemplate, ChevronsRight,
} from "lucide-react";
import api from "@/lib/api";
import { useCockpit } from "./CockpitLayout";
import { Card, BtnPrimary, BtnSecondary, SectionLabel } from "./ui";
import { Spinner, chf } from "@/components/common";
import { useI18n } from "@/context/I18nContext";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return iso; } };
const recIcon = (title = "") => {
  const s = title.toLowerCase();
  if (s.includes("local")) return MapPin;
  if (s.includes("seo")) return Rocket;
  if (s.includes("bewertung") || s.includes("google")) return MessageSquare;
  if (s.includes("app")) return LayoutGrid;
  if (s.includes("content") || s.includes("inhalt") || s.includes("redakt")) return FileText;
  if (s.includes("anfrage") || s.includes("projekt")) return FileText;
  return Sparkles;
};
const PHASES = [
  { n: 1, title: "Freigaben", desc: "Ihre Inhalte, Bilder & Texte" },
  { n: 2, title: "Umsetzung", desc: "Wir setzen für Sie um" },
  { n: 3, title: "Optimierung", desc: "Performance & Feinschliff" },
  { n: 4, title: "Ausbau", desc: "Neue Funktionen & Wachstum" },
];
const WHY = [
  "Mehr Sichtbarkeit und bessere Rankings bringen Ihnen qualifizierte Anfragen.",
  "Stärkere Kundenbindung und Vertrauen durch moderne Funktionen und Bewertungen.",
  "Gezielte Inhalte und lokale Präsenz steigern Ihre Abschlüsse nachhaltig.",
];

export default function CNextSteps() {
  const { accent, clientId, base, overview, openAi } = useCockpit();
  const { t } = useI18n();
  const nav = useNavigate();
  const [steps, setSteps] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [design, setDesign] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(null);
  const suffix = clientId ? `&client_id=${clientId}` : "";
  const qs = clientId ? `?client_id=${clientId}` : "";

  const loadDesign = () => api.get(`/cockpit/design-ideas${qs}`).then((r) => setDesign(r.data.ideas || [])).catch(() => setDesign([]));
  const load = () => {
    api.get(`/cockpit/next-steps?lang=de${suffix}`).then((r) => setSteps(r.data));
    api.get(`/cockpit/next-steps/summary${qs}`).then((r) => setSummary(r.data)).catch(() => setSummary({}));
  };
  useEffect(() => {
    load();
    loadDesign();
    api.get(`/cockpit/tasks${qs}`).then((r) => setTasks(r.data.filter((tk) => !["erledigt", "archiviert"].includes(tk.status))));
  }, [clientId]); // eslint-disable-line
  if (!steps || !summary) return <Spinner />;

  const approveDesign = async (id) => {
    setBusy(id);
    try {
      await api.post(`/cockpit/design-ideas/${id}/approve${qs}`);
      toast.success(t("Zur Umsetzung freigegeben — danke!"));
      loadDesign();
    } catch (e) { toast.error(e.response?.data?.detail || t("Aktion fehlgeschlagen")); }
    finally { setBusy(null); }
  };

  const tellInterest = async (s) => {
    setBusy(s.id);
    try {
      await api.post(`/cockpit/next-steps/${s.id}/interest${qs}`);
      toast.success(t("Interesse gemeldet — wir melden uns bei Ihnen!"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Konnte nicht senden.")); }
    finally { setBusy(null); }
  };
  const goSupport = () => openAi();
  const goTasks = () => nav(`${base}/tasks`);
  const scrollToRecs = () => document.getElementById("cns-recommendations")?.scrollIntoView({ behavior: "smooth", block: "start" });

  const recs = steps.filter((s) => !s.interested);
  const designProposals = design.filter((d) => d.needs_client_approval && ["empfohlen", "geplant", "wartet_auf_kunde"].includes(d.status));
  const designInProgress = design.filter((d) => ["freigegeben", "in_arbeit", "online"].includes(d.status));
  const dTone = (s) => (s === "online" || s === "freigegeben") ? { color: accent, bg: `${accent}1A` } : { color: "#fbbf24", bg: "rgba(251,191,36,0.12)" };
  const openSteps = summary.open_steps ?? tasks.length;
  const contact = (overview.care_team && overview.care_team[0]) || null;
  const sc = overview.support_contact || {};
  const phaseStatus = (n) => (n === 1 ? (openSteps > 0 ? "Aktiv" : "Erledigt") : n === 2 ? "In Arbeit" : "Geplant");

  const stats = [
    { icon: ClipboardList, value: summary.open_steps ?? 0, label: t("Offene nächste Schritte"), sub: t("Benötigen Ihre Freigabe"), highlight: true },
    { icon: Star, value: summary.recommendations ?? 0, label: t("Empfehlungen"), sub: t("Für mehr Anfragen") },
    { icon: Megaphone, value: summary.interests ?? 0, label: t("Interessen gemeldet"), sub: t("Potenziale in Prüfung") },
    { icon: CheckCircle, value: summary.done_this_month ?? 0, label: t("Erledigt diesen Monat"), sub: t("Tolle Fortschritte!") },
  ];

  return (
    <div className="fade-in space-y-5" data-testid="cockpit-next-steps">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[26px] lg:text-[34px] leading-tight font-black tracking-tight">{t("Nächste Schritte")}</h1>
          <p className="text-sm text-gray-400 mt-1.5 max-w-2xl">{t("Was jetzt ansteht und wie wir Ihren Auftritt weiter ausbauen können.")}</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto sm:shrink-0">
          <BtnSecondary className="flex-1 sm:flex-none" data-testid="cns-header-interest" onClick={scrollToRecs}><Megaphone className="w-4 h-4" /> {t("Interesse melden")}</BtnSecondary>
          <BtnPrimary className="flex-1 sm:flex-none" accent={accent} data-testid="cns-header-ask" onClick={goSupport}>{t("Frage stellen")} <ChevronRight className="w-4 h-4" /></BtnPrimary>
        </div>
      </div>

      {/* Oben: Jetzt zu erledigen + Stats/Fahrplan */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,38%)_1fr] gap-4 items-start">
        {/* Jetzt zu erledigen */}
        <Card className="p-5" data-testid="cns-todo">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-5 h-5" style={{ color: accent }} />
            <span className="font-bold">{t("Jetzt zu erledigen")}</span>
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-white/5 text-gray-400 border border-white/10">{tasks.length} {t("offen")}</span>
          </div>
          {tasks.length === 0 ? (
            <p className="text-sm text-gray-500 py-2">{t("Alles erledigt — aktuell steht nichts an.")}</p>
          ) : (
            <div>
              {tasks.slice(0, 4).map((tk) => {
                const Ico = tk.needs_client_approval ? CircleDot : Circle;
                return (
                  <button key={tk.id} onClick={goTasks} data-testid={`cns-task-${tk.id}`}
                    className="w-full flex items-center gap-3 py-3 border-b border-white/5 last:border-0 text-left hover:opacity-90 transition-opacity">
                    <Ico className="w-5 h-5 shrink-0" style={{ color: tk.needs_client_approval ? accent : "#6b7280" }} />
                    <span className="text-sm flex-1 truncate">{tk.title}</span>
                    {tk.due_date && <span className="text-xs text-gray-500 shrink-0 hidden sm:inline">{t("Fällig")}: {fmtDate(tk.due_date)}</span>}
                    <ChevronRight className="w-4 h-4 text-gray-600 shrink-0" />
                  </button>
                );
              })}
              <div className="flex justify-center pt-3">
                <button onClick={goTasks} data-testid="cns-all-steps" className="text-sm font-semibold flex items-center gap-1 hover:opacity-80 transition-opacity" style={{ color: accent }}>
                  {t("Alle nächsten Schritte anzeigen")} <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </Card>

        {/* Stats + Fahrplan */}
        <div className="space-y-4">
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
            {stats.map((s, i) => (
              <div key={i} data-testid={`cns-stat-${i}`}
                className={`rounded-2xl p-4 border ${s.highlight ? "bg-[#171b14]" : "bg-[#15181e] border-white/5"}`}
                style={s.highlight ? { borderColor: `${accent}66` } : {}}>
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}1F`, color: accent }}><s.icon className="w-4.5 h-4.5" /></span>
                  <span className="text-3xl font-black leading-none tracking-tight">{s.value}</span>
                </div>
                <div className="text-sm font-semibold mt-2.5">{s.label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{s.sub}</div>
              </div>
            ))}
          </div>

          <Card className="p-5" data-testid="cns-roadmap">
            <SectionLabel>{t("Ihr Fahrplan – Nächste 3 Phasen")}</SectionLabel>
            <div className="flex items-stretch gap-1 mt-3 overflow-x-auto">
              {PHASES.map((p, i) => {
                const st = phaseStatus(p.n);
                const active = st === "Aktiv" || st === "Erledigt";
                const working = st === "In Arbeit";
                return (
                  <div key={p.n} className="flex-1 min-w-[130px]">
                    <div className="flex items-center">
                      <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm font-bold"
                        style={active ? { background: accent, color: "#000" } : working ? { background: `${accent}22`, color: accent, border: `1px solid ${accent}66` } : { background: "rgba(255,255,255,0.05)", color: "#6b7280", border: "1px solid rgba(255,255,255,0.1)" }}>
                        {active ? <CheckCircle2 className="w-4 h-4" /> : working ? <CircleDot className="w-4 h-4" /> : p.n}
                      </span>
                      {i < PHASES.length - 1 && <span className="h-0.5 flex-1 mx-1" style={{ background: active ? accent : "rgba(255,255,255,0.1)" }} />}
                    </div>
                    <div className="mt-2 font-bold text-sm">{p.n}. {t(p.title)}</div>
                    <div className="text-xs text-gray-500">{t(p.desc)}</div>
                    <span className="inline-block mt-2 text-[10px] font-bold px-2 py-0.5 rounded-full"
                      style={st === "Aktiv" ? { background: accent, color: "#000" } : working ? { background: `${accent}1A`, color: accent } : st === "Erledigt" ? { background: `${accent}1A`, color: accent } : { background: "rgba(255,255,255,0.05)", color: "#9ca3af" }}>
                      {t(st)}
                    </span>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      </div>

      {/* Empfohlen für mehr Anfragen */}
      <div id="cns-recommendations">
        <div className="flex items-center gap-2 mb-1"><Star className="w-5 h-5" style={{ color: accent }} /><h2 className="font-bold text-lg">{t("Empfohlen für mehr Anfragen")}</h2></div>
        <p className="text-sm text-gray-500 mb-4">{t("Optionale Leistungen, die Ihnen helfen können, mehr Anfragen und Kunden zu gewinnen.")}</p>
        {steps.length === 0 ? (
          <Card className="p-6 text-center text-sm text-gray-500">{t("Aktuell keine Empfehlungen — Ihr Auftritt ist auf dem neuesten Stand.")}</Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {steps.map((s) => {
              const Ico = recIcon(s.title);
              const price = s.module && s.module.price_chf ? ` · ${t("ab")} ${chf(s.module.price_chf)}${t("/ Mt.")}` : "";
              const desc = s.description && s.description !== s.benefit ? s.description : (s.module?.benefit || s.module?.description || "");
              return (
                <Card key={s.id} className="p-4 flex flex-col" data-testid={`cnextstep-${s.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}1A`, color: accent }}><Ico className="w-5 h-5" /></span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${accent}1A`, color: accent }}>{t("Empfohlen")}</span>
                  </div>
                  <div className="font-bold mt-3 leading-snug">{s.title}</div>
                  {s.benefit && <div className="text-sm font-semibold text-gray-200 mt-1">{s.benefit}</div>}
                  {desc && <p className="text-sm text-gray-400 mt-0.5 flex-1">{desc}</p>}
                  {!desc && <div className="flex-1" />}
                  <div className="text-xs mt-3" style={{ color: s.included ? accent : "#9ca3af" }}>{t(s.offer_label)}{price}</div>
                  {s.interested ? (
                    <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold" style={{ color: accent }}><CheckCircle2 className="w-4 h-4" /> {t(s.status_label)}</div>
                  ) : (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <button onClick={() => tellInterest(s)} disabled={busy === s.id} data-testid={`interest-${s.id}`}
                        className="text-black text-xs font-bold rounded-lg px-2 py-2 disabled:opacity-50" style={{ background: accent }}>
                        {busy === s.id ? t("Senden…") : t("Interesse melden")}
                      </button>
                      <BtnSecondary data-testid={`ask-${s.id}`} onClick={goSupport} className="text-xs !px-2 !py-2 justify-center"><MessageCircle className="w-3.5 h-3.5" /> {t("Frage stellen")}</BtnSecondary>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Design- & Growth-Vorschläge: Report -> Freigabe -> Umsetzung */}
      {design.length > 0 && (
        <div data-testid="cns-design">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
            <div className="flex items-center gap-2">
              <LayoutTemplate className="w-5 h-5" style={{ color: accent }} />
              <h2 className="font-bold text-lg">{t("Verbesserungsvorschläge")}</h2>
              {designProposals.length > 0 && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${accent}1A`, color: accent }}>{designProposals.length} {t("zur Freigabe")}</span>}
            </div>
            <button onClick={() => nav(`${base}/design`)} data-testid="cns-design-all" className="text-sm font-semibold flex items-center gap-1 hover:opacity-80" style={{ color: accent }}>
              {t("Alle ansehen")} <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-4">{t("Konkrete Verbesserungen von KEWAG — geben Sie sie hier direkt zur Umsetzung frei.")}</p>

          {designProposals.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {designProposals.map((d) => (
                <Card key={d.id} className="p-4 flex flex-col" data-testid={`cns-design-${d.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}1A`, color: accent }}><LayoutTemplate className="w-5 h-5" /></span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${accent}1A`, color: accent }}>{d.status_label}</span>
                  </div>
                  <div className="font-bold mt-3 leading-snug">{d.title}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{d.category_label}</div>
                  {d.expected_benefit && <p className="text-sm mt-1.5 flex-1" style={{ color: accent }}>{t("Nutzen")}: {d.expected_benefit}</p>}
                  {!d.expected_benefit && (d.client_hint ? <p className="text-sm text-gray-400 mt-1.5 flex-1">{d.client_hint}</p> : <div className="flex-1" />)}
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <button onClick={() => approveDesign(d.id)} disabled={busy === d.id} data-testid={`cns-design-approve-${d.id}`}
                      className="text-black text-xs font-bold rounded-lg px-2 py-2 disabled:opacity-50 flex items-center justify-center gap-1" style={{ background: accent }}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> {busy === d.id ? t("Senden…") : t("Freigeben")}
                    </button>
                    <BtnSecondary data-testid={`cns-design-details-${d.id}`} onClick={() => nav(`${base}/design`)} className="text-xs !px-2 !py-2 justify-center"><ChevronsRight className="w-3.5 h-3.5" /> {t("Details")}</BtnSecondary>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-5 text-sm text-gray-400" data-testid="cns-design-none-open">{t("Aktuell keine Vorschläge zur Freigabe. Sobald KEWAG neue Verbesserungen vorbereitet, sehen Sie sie hier.")}</Card>
          )}

          {designInProgress.length > 0 && (
            <Card className="p-5 mt-3" data-testid="cns-design-progress">
              <SectionLabel>{t("In Umsetzung")}</SectionLabel>
              <div className="mt-2 divide-y divide-white/5">
                {designInProgress.map((d) => {
                  const tone = dTone(d.status);
                  return (
                    <div key={d.id} className="py-2.5 flex items-center justify-between gap-3">
                      <span className="text-sm text-gray-200 min-w-0 truncate">{d.title}</span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0" style={{ background: tone.bg, color: tone.color }}>{d.status_label}</span>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Unten: Warum / Eigene Idee / Betreuung */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 relative overflow-hidden" data-testid="cns-why">
          <div className="flex items-center gap-2 mb-3"><Lightbulb className="w-5 h-5" style={{ color: accent }} /><span className="font-bold">{t("Warum diese Empfehlungen?")}</span></div>
          <div className="space-y-2.5 relative z-10">
            {WHY.map((w, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-gray-300"><CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color: accent }} /> {t(w)}</div>
            ))}
          </div>
          <TrendingUp className="w-24 h-24 absolute -right-3 -bottom-3 opacity-[0.06]" style={{ color: accent }} />
        </Card>

        <Card className="p-5 flex flex-col" data-testid="cns-idea">
          <div className="flex items-center gap-2 mb-2"><MessageCircle className="w-5 h-5" style={{ color: accent }} /><span className="font-bold">{t("Haben Sie eine eigene Idee?")}</span></div>
          <p className="text-sm text-gray-400 flex-1">{t("Schreiben Sie uns — wir beraten Sie gerne zu Ihrem Projekt.")}</p>
          <div className="flex items-center gap-2 mt-4">
            <BtnPrimary accent={accent} data-testid="cns-idea-ask" onClick={goSupport}>{t("Frage stellen")}</BtnPrimary>
            <BtnSecondary data-testid="cns-idea-message" onClick={goSupport}><Send className="w-4 h-4" /> {t("Nachricht senden")}</BtnSecondary>
          </div>
        </Card>

        <Card className="p-5" data-testid="cns-care">
          <div className="flex items-start gap-3">
            {contact?.picture
              ? <img src={contact.picture} alt={contact.name} className="w-11 h-11 rounded-full object-cover shrink-0" />
              : <div className="w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold shrink-0" style={{ background: `${accent}22`, color: accent }}>{(contact?.name || "KEWAG").split(" ").map((p) => p[0]).slice(0, 2).join("")}</div>}
            <div className="min-w-0">
              <div className="font-bold text-sm">{t("Betreuung durch KEWAG")}</div>
              <div className="text-sm text-gray-300 truncate">{contact?.name || t("KEWAG Support-Team")}</div>
              <div className="text-xs text-gray-500">{contact ? t(contact.function) : t("Ihr digitaler Partner")}</div>
            </div>
          </div>
          <div className="mt-4 space-y-2 text-sm">
            {sc.phone && <a href={`tel:${sc.phone.replace(/\s/g, "")}`} data-testid="cns-care-phone" className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"><Phone className="w-4 h-4" style={{ color: accent }} /> {sc.phone}</a>}
            {sc.email && <a href={`mailto:${sc.email}`} data-testid="cns-care-email" className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"><Mail className="w-4 h-4" style={{ color: accent }} /> {sc.email}</a>}
            <div className="flex items-center gap-2 text-gray-400"><Star className="w-4 h-4" style={{ color: accent }} /> {t("Persönlicher Support")}</div>
            <div className="flex items-center gap-2 text-gray-400"><Clock className="w-4 h-4" style={{ color: accent }} /> {t("Schnelle Reaktionszeiten")}</div>
          </div>
          <BtnPrimary accent={accent} data-testid="cns-care-message" onClick={goSupport} className="w-full mt-4 !py-2.5"><Send className="w-4 h-4" /> {t("Nachricht senden")}</BtnPrimary>
        </Card>
      </div>
    </div>
  );
}
